import { FC, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { BUTTON_VARIANTS, BUTTON_SIZES, IconButton } from 'ui/button';
import { ToBottom16, ToTop16 } from 'ui/icons';
import { duration, easing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';

const StyledButtonTooltip = styled(Tooltip)`
  position: absolute;
  bottom: ${pxToRem(10)};
  right: ${pxToRem(10)};
  opacity: 0;
  transition: opacity ${duration.medium} ${easing.entrance} ${duration.short};
`;

const Content = styled.div`
  overflow-x: hidden;
  
  :hover {
    ${StyledButtonTooltip} {
      opacity: 1;
    }
  }
`;

type ExpandableModuleContentProps = {
  isExpanded?: boolean;
  onViewMoreClick?: () => void;
  showViewMore?: boolean;
  isLoadingMore?: boolean;
};

export const testIds = {
  content: 'expandable-module-content',
  viewMoreButton: 'view-more-button',
};

export const ExpandableModuleContent: FC<PropsWithChildren<ExpandableModuleContentProps>> = ({
  isExpanded,
  onViewMoreClick,
  showViewMore,
  children,
  isLoadingMore,
  ...props
}) => {
  return (
    <Content data-test={testIds.content} {...props}>
      {children}

      {showViewMore && (
        <StyledButtonTooltip tooltipContent={`View ${isExpanded ? 'less' : 'more'}`} isEnabled>
          <IconButton
            data-test={testIds.viewMoreButton}
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.GHOST}
            isLoading={isLoadingMore}
            onClick={() => {
              if (onViewMoreClick) onViewMoreClick();
            }}
          >
            {isExpanded ? (
              <ToTop16 data-test="top-arrow" />
            ) : (
              <ToBottom16 data-test="bottom-arrow" />
            )}
          </IconButton>
        </StyledButtonTooltip>
      )}
    </Content>
  );
};
